import * as React from 'react';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { EGDSHeading, EGDSText } from '@egds/react-core/text';
import { EGDSLayoutFlex, EGDSLayoutFlexItem } from '@egds/react-core/layout-flex';
import { EGDSCard } from '@egds/react-core/cards';
import { EGDSButtonSize, EGDSSecondaryButton } from '@egds/react-core/button';
import { EGDSIcon } from '@egds/react-core/icons';
import { TripInfoDetailFields } from 'common/__generated__/api/types';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';
import { buildClickStreamForBookingActions } from 'common/helper';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import TripInfos = TripInfoDetailFields.tripInfos;
import { ClientLogger } from 'bernie-client';
import { EVENTS } from 'utils/logger-events';
import { VirtualAgentControlClientConfig } from '@shared-ui/customer-virtual-agent-control';
import { useVirtualAgentContext } from '@shared-ui/retail-help-center-util';

type StandAloneVAUrlParam = Pick<
  VirtualAgentControlClientConfig,
  'intentMessage' | 'conversationId' | 'participantId' | 'mode'
>;

interface TripInfoDetailsProps {
  setTripInfoData?: (value) => void;
  tripInfoData: TripInfos;
  mobileView?: boolean;
}

const logger = ClientLogger.getLoggerWithIdentifier('TripInfoDetail');

const TripInfoDetail: React.FC<TripInfoDetailsProps> = ({
  setTripInfoData,
  tripInfoData,
  mobileView = false,
}: TripInfoDetailsProps) => {
  const trackClick = useEgClickstream();
  const { controller } = useVirtualAgentContext();

  return (
    <>
      <Viewport>
        <ViewSmall>
          <EGDSSpacing margin={{ blockend: 'six' }}>
            <EGDSSecondaryButton size={EGDSButtonSize.LARGE} onClick={() => setTripInfoData(null)}>
              <EGDSIcon name={'arrow_back_iOS'} />
              {tripInfoData.backToTripButton}
            </EGDSSecondaryButton>
          </EGDSSpacing>
        </ViewSmall>
        {!mobileView ? <ViewMedium /> : <></>}
      </Viewport>
      <EGDSCard>
        {tripInfoData.tripBookings.map((booking, index) => {
          if (booking.__typename == 'BookingDetail') {
            return (
              <EGDSSpacing margin={{ blockend: 'two' }} key={`trip-detail-${index}`}>
                <EGDSCard padded border>
                  <EGDSLayoutFlex space="two" alignItems="center">
                    <EGDSLayoutFlexItem>
                      <EGDSIcon name={booking.icon.id} />
                    </EGDSLayoutFlexItem>
                    <EGDSLayoutFlexItem>
                      <div>
                        <EGDSHeading tag="h4" size={6}>
                          {booking.title}
                        </EGDSHeading>
                        <EGDSText size={200}>{booking.timePeriod}</EGDSText>
                        <EGDSText size={200}>{booking.itineraryNumberText}</EGDSText>
                      </div>
                    </EGDSLayoutFlexItem>
                  </EGDSLayoutFlex>

                  <EGDSSpacing margin={{ blockstart: 'two' }}>
                    <EGDSLayoutFlex space="three" alignItems="end" wrap={'wrap'}>
                      {booking.bookingActions.map((action, index) => (
                        <EGDSLayoutFlexItem key={`trip-action-${index}`}>
                          {action.__typename == 'HelpCenterIntentButtons' ? (
                            <EGDSSecondaryButton
                              type="button"
                              size={EGDSButtonSize.SMALL}
                              onClick={async () => {
                                const message: StandAloneVAUrlParam['intentMessage'] = {
                                  text: action.vaText,
                                  messageType: 'POSTBACK',
                                  intent: action.skillName,
                                };
                                if (action.payload) {
                                  message.payload = action.payload;
                                }
                                logger.logEvent(EVENTS.PERSONALIZATION_INTENT_CLICK, {
                                  message: 'PERSONALIZATION_INTENT_CLICK',
                                  intent: message.intent,
                                  product: booking.product,
                                });
                                controller.sendMessage(message);
                                trackClick(
                                  buildClickStreamForBookingActions(action.bookingActionsClickStreamAnalytics)
                                );
                              }}
                            >
                              {action.name.primary}
                            </EGDSSecondaryButton>
                          ) : (
                            <EGDSSecondaryButton
                              type="button"
                              tag="a"
                              target="_blank"
                              size={EGDSButtonSize.SMALL}
                            ></EGDSSecondaryButton>
                          )}
                        </EGDSLayoutFlexItem>
                      ))}
                    </EGDSLayoutFlex>
                  </EGDSSpacing>
                </EGDSCard>
              </EGDSSpacing>
            );
          } else if (booking.__typename == 'UnSupportedBooking') {
            return (
              <EGDSCard padded border>
                <EGDSSpacing margin={{ blockstart: 'two' }}>
                  <EGDSHeading tag="h4" size={6}>
                    {booking.heading}
                  </EGDSHeading>
                </EGDSSpacing>
                <EGDSText size={300}>{booking.description}</EGDSText>
                <EGDSSpacing margin={{ blockstart: 'three' }}>
                  <EGDSSecondaryButton
                    type="button"
                    tag="a"
                    href={
                      booking.goToTrips.action.__typename == 'UILinkAction'
                        ? booking.goToTrips.action.resource.value
                        : ''
                    }
                    target="_blank"
                    size={EGDSButtonSize.SMALL}
                    onClick={async () => {
                      trackClick(buildClickStreamForBookingActions(booking.clickstreamPayload));
                    }}
                  >
                    <EGDSIcon name={booking.goToTrips.icon.id} />
                    {booking.goToTrips.primary}
                  </EGDSSecondaryButton>
                </EGDSSpacing>
              </EGDSCard>
            );
          }
        })}
      </EGDSCard>
    </>
  );
};

export default TripInfoDetail;
